<template>
  <v-container fluid class="bg-light-soft min-h-screen pa-6">
    <div v-if="accounts_info != null">
      <!-- Header Section -->
      <v-row no-gutters class="mb-8 align-center">
        <v-col cols="12" md="8">
          <div class="d-flex align-center">
            <v-btn @click="$router.back()" icon class="mr-4 bg-white shadow-sm rounded-lg" color="primary">
              <v-icon>mdi-arrow-left</v-icon>
            </v-btn>
            <div>
              <div class="d-flex align-center mb-1">
                <v-avatar size="32" class="mr-3 shadow-xs">
                  <v-img contain :src="getIconBank(accounts_info.account_bank_id)"></v-img>
                </v-avatar>
                <span class="text-overline grey--text font-weight-black letter-spacing-1">Detalle de Cuenta
                  Bancaria</span>
              </div>
              <h1 class="text-h4 font-weight-black grey--text text--darken-4">
                {{ accounts_info.bank_description_long }}
                <span class="primary--text ml-2">#{{ accounts_info.account_check_digit }}</span>
              </h1>
            </div>
          </div>
        </v-col>
        <v-col cols="12" md="4" class="text-right">
          <v-btn color="primary" depressed class="rounded-xl px-6 font-weight-bold" @click="searchLoaded">
            <v-icon left>mdi-refresh</v-icon> Actualizar
          </v-btn>
        </v-col>
      </v-row>

      <!-- Summary Cards -->
      <v-row class="mb-8">
        <!-- Account Info Card -->
        <v-col cols="12" md="3">
          <v-card class="rounded-xl border-soft shadow-sm h-full transition-all hover-bg-light" elevation="0">
            <v-card-text class="pa-5">
              <div class="d-flex align-center mb-4">
                <v-avatar color="indigo lighten-5" size="40" class="mr-3">
                  <v-icon color="indigo">mdi-bank</v-icon>
                </v-avatar>
                <div class="d-flex flex-column">
                  <span class="text-tiny text-uppercase font-weight-bold grey--text letter-spacing-1">Información</span>
                  <span class="text-subtitle-2 font-weight-bold indigo--text">Cuenta Corriente</span>
                </div>
              </div>
              <div class="mb-3">
                <div class="text-caption grey--text mb-1">Nombre de Cuenta</div>
                <div class="text-h6 font-weight-bold grey--text text--darken-3">{{ accounts_info.account_name }}</div>
              </div>
              <div class="d-flex align-center">
                <v-chip x-small color="indigo" dark class="font-weight-bold mr-2">{{ accounts_info.currency_code
                  }}</v-chip>
                <span class="text-caption grey--text">{{ accounts_info.currency_description }}</span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Balance Card -->
        <v-col cols="12" md="6">
          <v-card class="rounded-xl border-soft shadow-sm h-full bg-gradient-success" elevation="0" dark>
            <v-card-text class="pa-5 d-flex flex-column justify-center fill-height">
              <v-row align="center">
                <v-col cols="7">
                  <div class="text-overline white--text opacity-80 font-weight-bold letter-spacing-1">Saldo Disponible
                    en Sistema</div>
                  <div class="d-flex align-baseline mt-1">
                    <span class="text-h3 font-weight-black white--text mr-2">{{ currencyFormat(total_amount) }}</span>
                    <span class="text-h6 font-weight-bold white--text opacity-70">{{ accounts_info.currency_code
                      }}</span>
                  </div>
                </v-col>
                <v-divider vertical class="mx-4 white opacity-20"></v-divider>
                <v-col cols="4">
                  <div class="d-flex flex-column">
                    <span class="text-tiny text-uppercase font-weight-bold opacity-70 mb-1">Última Transacción</span>
                    <div class="d-flex align-center">
                      <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>
                      <span class="text-subtitle-2 font-weight-bold">{{ lastTransactionDate }}</span>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Accounting Details Card -->
        <v-col cols="12" md="3">
          <v-card class="rounded-xl border-soft shadow-sm h-full transition-all" elevation="0">
            <v-card-text class="pa-5">
              <div class="d-flex align-center mb-4">
                <v-avatar color="amber lighten-5" size="40" class="mr-3">
                  <v-icon color="amber darken-3">mdi-book-open-variant</v-icon>
                </v-avatar>
                <div class="d-flex flex-column">
                  <span
                    class="text-tiny text-uppercase font-weight-bold grey--text letter-spacing-1">Contabilidad</span>
                  <span class="text-subtitle-2 font-weight-bold amber--text text--darken-4">Auxiliar Contable</span>
                </div>
              </div>
              <div class="mb-3">
                <div class="text-caption grey--text mb-1">Código de Cuenta</div>
                <div class="text-subtitle-1 font-weight-black grey--text text--darken-3">{{
                  accounts_info.chart_of_account_code }}</div>
              </div>
              <div class="text-caption font-weight-bold grey--text text-truncate">
                {{ accounts_info.chart_of_account_name }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Transactions Table Section -->
      <v-card class="rounded-xl border-soft shadow-sm overflow-hidden" elevation="0">
        <v-toolbar flat class="px-4 border-bottom bg-white" height="72">
          <v-icon color="primary" class="mr-3">mdi-swap-horizontal</v-icon>
          <v-toolbar-title class="font-weight-black grey--text text--darken-3">Movimientos Recientes</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-text-field v-model="search" prepend-inner-icon="mdi-magnify"
            label="Buscar por código, tercero o descripción..." single-line hide-details outlined dense rounded
            class="max-width-300" bg-color="grey lighten-5"></v-text-field>
        </v-toolbar>

        <v-data-table :headers="headers" :items="dataDiary" :search="search" :loading="loading" class="custom-table"
          :footer-props="{
            'items-per-page-text': 'Filas por página',
            'items-per-page-options': [15, 30, 50, 100]
          }" :items-per-page="15">
          <!-- Custom item rendering -->
          <template v-slot:[`item.date`]="{ item }">
            <div class="d-flex flex-column py-2">
              <span class="text-body-2 font-weight-black grey--text text--darken-4">{{ getDate(item.effective_date)
                }}</span>
              <span class="text-tiny grey--text">{{ getTime(item.effective_date) }}</span>
            </div>
          </template>

          <template v-slot:[`item.diary_book_code`]="{ item }">
            <div class="d-flex align-center">
              <v-avatar size="28" :color="getTypeColor(item.diary_book_type_code)" class="mr-2" dark>
                <span class="text-tiny font-weight-black white--text">{{ item.diary_book_type_code }}</span>
              </v-avatar>
              <span class="text-body-2 font-weight-bold primary--text cursor-pointer hover-underline"
                @click="viewDetail(item)">
                {{ item.diary_book_code }}
              </span>
            </div>
          </template>

          <template v-slot:[`item.entity`]="{ item }">
            <div class="d-flex align-center py-1">
              <v-avatar size="32" color="grey lighten-4" class="mr-3 border shadow-xs">
                <span class="text-tiny font-weight-black grey--text">{{ getInitials(item.entity_name || 'N/A') }}</span>
              </v-avatar>
              <div class="d-flex flex-column">
                <span class="text-body-2 font-weight-bold grey--text text--darken-4 text-truncate"
                  style="max-width: 250px;">
                  {{ item.entity_name || 'Sin Tercero Asignado' }}
                </span>
                <span class="text-tiny grey--text">{{ item.diary_book_type_description }}</span>
              </div>
            </div>
          </template>

          <template v-slot:[`item.amount_in`]="{ item }">
            <div class="text-right" v-if="item.entry_type_id == 1">
              <div class="d-flex flex-column align-end">
                <span class="success--text font-weight-black text-body-2">+ {{ currencyFormat(item.amount) }}</span>
                <span class="text-tiny grey--text">{{ item.currency_code }}</span>
              </div>
            </div>
          </template>

          <template v-slot:[`item.amount_out`]="{ item }">
            <div class="text-right" v-if="item.entry_type_id == 2">
              <div class="d-flex flex-column align-end">
                <span class="error--text font-weight-black text-body-2">- {{ currencyFormat(item.amount) }}</span>
                <span class="text-tiny grey--text">{{ item.currency_code }}</span>
              </div>
            </div>
          </template>

          <template v-slot:[`item.is_conciliated`]="{ item }">
            <v-chip x-small :color="item.is_conciliated ? 'success lighten-5' : 'grey lighten-4'"
              :text-color="item.is_conciliated ? 'success darken-2' : 'grey darken-1'" class="font-weight-black px-3"
              label>
              <v-icon left x-small v-if="item.is_conciliated">mdi-check-circle</v-icon>
              {{ item.is_conciliated ? 'CONCILIADO' : 'PENDIENTE' }}
            </v-chip>
          </template>

          <template v-slot:[`item.status`]="{ item }">
            <v-chip x-small :color="getColor(item.status_id)" dark class="font-weight-black">
              {{ getStatusName(item.status_id) }}
            </v-chip>
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <div class="d-flex align-center">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon small color="primary" v-bind="attrs" v-on="on" @click="viewDetail(item)"
                    class="bg-blue-soft rounded-lg">
                    <v-icon small>mdi-eye</v-icon>
                  </v-btn>
                </template>
                <span>Ver Detalle</span>
              </v-tooltip>

              <v-tooltip bottom v-if="!item.is_conciliated">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon small color="success" v-bind="attrs" v-on="on" @click="updateStatusAccount(item)"
                    class="ml-2 bg-green-soft rounded-lg">
                    <v-icon small>mdi-link-variant</v-icon>
                  </v-btn>
                </template>
                <span>Conciliar Movimiento</span>
              </v-tooltip>
            </div>
          </template>

          <!-- Empty state -->
          <template v-slot:no-data>
            <div class="py-12 text-center d-flex flex-column align-center">
              <v-icon size="64" color="grey lighten-4">mdi-database-off</v-icon>
              <div class="text-h6 grey--text text--lighten-1 mt-4">No se encontraron movimientos registrados</div>
              <v-btn text color="primary" class="mt-2 text-capitalize" @click="searchLoaded">Recargar datos</v-btn>
            </div>
          </template>

        </v-data-table>
      </v-card>
    </div>

    <!-- Loading Overlay -->
    <v-overlay v-if="loading" absolute z-index="10" opacity="0.3">
      <v-progress-circular indeterminate size="64" color="primary" width="6"></v-progress-circular>
    </v-overlay>
  </v-container>
</template>

<script>
import moment from "moment";

export default {
  data: () => ({
    loading: false,
    search: "",
    dataDiary: [],
    accounts_info: null,
    headers: [
      { text: "Fecha", value: "date", width: "120" },
      { text: "Documento", value: "diary_book_code", width: "180" },
      { text: "Tercero / Descripción", value: "entity" },
      { text: "Conciliación", value: "is_conciliated", align: "center", width: "130" },
      { text: "Salidas", value: "amount_out", align: "end", width: "140" },
      { text: "Entradas", value: "amount_in", align: "end", width: "140" },
      { text: "Acciones", value: "actions", sortable: false, align: "center", width: "110" },
    ],
  }),

  computed: {
    total_amount() {
      if (!this.dataDiary) return 0;
      return this.dataDiary.reduce((acc, item) => {
        const amount = parseFloat(item.amount) || 0;
        if (item.entry_type_id === 1) {
          return acc + amount;
        } else if (item.entry_type_id === 2) {
          return acc - amount;
        }
        return acc;
      }, 0);
    },
    lastTransactionDate() {
      if (!this.dataDiary || this.dataDiary.length === 0) return "N/A";
      const dates = this.dataDiary.map(i => new Date(i.effective_date));
      const last = new Date(Math.max.apply(null, dates));
      return moment(last).format("DD/MM/YYYY");
    }
  },

  created() {
    this.initialize();
  },

  methods: {
    async initialize() {
      this.loading = true;
      try {
        const request = await this.api_Post("humanResource/businessAccountInfo", {
          id: this.$route.params.id,
        });
        if (request.status == 200) {
          const result = await request.json();
          this.accounts_info = result[0];
          await this.searchLoaded();
        }
      } catch (error) {
        this.$toast.error("Error al cargar información de la cuenta");
      } finally {
        this.loading = false;
      }
    },
    async searchLoaded() {
      this.loading = true;
      try {
        let filtro = "";
        filtro += "and business_unit_id=" + this.accounts_info.business_unit_id;
        filtro += " and CHART_OF_ACCOUNT_ID=" + this.accounts_info.chart_of_account_id;

        const responseRequest = await this.api_Post("accounting/diary_book_detail", {
          filtros: filtro,
        });

        if (responseRequest.status === 200) {
          this.dataDiary = await responseRequest.json();
        }
      } catch (error) {
        this.$toast.error("Error al cargar movimientos");
      } finally {
        this.loading = false;
      }
    },
    viewDetail(item) {
      // Determine destination based on type code if needed, otherwise fallback to a general view
      if (item.diary_book_type_code === 'FV') {
        this.$router.push({ name: 'ViewInvoice', params: { id: item.invoice_id } });
      } else if (item.diary_book_type_code === 'FC') {
        this.$router.push({ name: 'ViewInvoice', params: { id: item.invoice_id } });
      } else if (item.diary_book_type_code === 'CP') {
        this.$router.push({ name: 'ViewPayments', params: { id: item.payment_id } });
      } else {
        this.$toast.info("Visualización directa no disponible para este tipo", { position: "bottom-right" });
      }
    },
    getTypeColor(code) {
      const colors = {
        'FV': 'teal darken-1',
        'FC': 'indigo darken-1',
        'ED': 'amber darken-2',
        'CP': 'deep-purple darken-1',
        'NC': 'red darken-1',
        'ND': 'blue darken-1'
      };
      return colors[code] || 'grey darken-2';
    },
    getStatusName(id) {
      const statuses = { 1: "Pendiente", 2: "Completado", 3: "Anulado", 4: "Error" };
      return statuses[id] || "N/A";
    },
    getColor(color) {
      const colors = { 1: "orange", 2: "success", 3: "error", 4: "error" };
      return colors[color] || "grey";
    },
    getDate(date) {
      return moment(date).format("DD/MM/YYYY");
    },
    getTime(date) {
      return moment(date).format("hh:mm a");
    },
    getInitials(name) {
      if (!name) return "??";
      return name.split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase();
    },
    updateStatusAccount(item) {
      this.$toast.info("Conciliación automática en desarrollo...", { position: "bottom-right" });
    }
  },
};
</script>

<style scoped>
.bg-light-soft {
  background-color: #f8fafc;
}

.border-soft {
  border: 1px solid rgba(0, 0, 0, 0.05) !important;
}

.border-bottom {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05) !important;
}

.border-right-light {
  border-right: 1px solid rgba(0, 0, 0, 0.05) !important;
}

.shadow-sm {
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06) !important;
}

.shadow-xs {
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05) !important;
}

.rounded-xl {
  border-radius: 16px !important;
}

.min-h-screen {
  min-height: 100vh;
}

.letter-spacing-1 {
  letter-spacing: 1px;
}

.text-tiny {
  font-size: 0.7rem;
  line-height: 1rem;
}

.opacity-80 {
  opacity: 0.8;
}

.opacity-70 {
  opacity: 0.7;
}

.opacity-20 {
  opacity: 0.2;
}

.max-width-300 {
  max-width: 300px;
}

.transition-all {
  transition: all 0.3s ease;
}

.hover-bg-light:hover {
  background-color: #f1f5f9;
  cursor: pointer;
}

.hover-underline:hover {
  text-decoration: underline;
}

.cursor-pointer {
  cursor: pointer;
}

.bg-blue-soft {
  background-color: #e0f2fe !important;
}

.bg-green-soft {
  background-color: #dcfce7 !important;
}

.bg-gradient-success {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
}

.custom-table ::v-deep th {
  background-color: #f8fafc !important;
  text-transform: uppercase;
  font-size: 0.75rem !important;
  font-weight: 700 !important;
  color: #64748b !important;
  letter-spacing: 0.05em;
  height: 48px !important;
}

.custom-table ::v-deep td {
  height: 64px !important;
  border-bottom: 1px solid #f1f5f9 !important;
}

.custom-table ::v-deep tr:hover {
  background-color: #f8fafc !important;
}

.custom-table ::v-deep .v-data-table__wrapper {
  border-radius: 0 0 16px 16px;
}
</style>
